/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, ListItem, ListItemButton, ListItemText } from '@mui/material';
import React from 'react';
import { ChevronDown, ChevronUp } from '@opengeoweb/theme';

interface CollectionListRowProps {
  title: string;
  count: number;
  level: number;
  expanded: boolean;
  onToggle: () => void;
}

export const CollectionListRow: React.FC<CollectionListRowProps> = ({
  title,
  count,
  level,
  expanded,
  onToggle,
}) => (
  <ListItem
    data-testid="layerListCollectionRow"
    sx={{
      backgroundColor: 'geowebColors.cards.cardContainer',
      height: '44px',
      marginTop: 0.5,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '3px',
      padding: 0,
      zIndex: 10,
      overflow: 'hidden',
      borderColor: 'geowebColors.cards.cardContainerBorder',
    }}
  >
    <ListItemButton
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
      onClick={onToggle}
      sx={{
        height: '100%',
        paddingLeft: `${24 + level * 20}px`,
        paddingRight: '8px',
      }}
    >
      <Grid
        container
        size="grow"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid size="grow">
          <ListItemText
            primary={title}
            sx={{
              margin: 0,
              '.MuiListItemText-primary': {
                fontSize: '12px',
                fontWeight: 500,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              },
            }}
          />
        </Grid>
        <Grid>
          <ListItemText
            primary={`(${count})`}
            sx={{
              paddingLeft: 1,
              paddingRight: 1,
              textAlign: 'right',
              '.MuiListItemText-primary': {
                fontSize: '12px',
                opacity: 0.67,
              },
            }}
          />
        </Grid>
      </Grid>
      {expanded ? <ChevronUp /> : <ChevronDown />}
    </ListItemButton>
  </ListItem>
);
